import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { METADATA } from '@flexo/reducers';
import { getOrgRole, isEndUser } from './roles';

type EndUserCommunitiesRedirectProps = {
  children: React.ReactNode;
};

/**
 * FLEXO-6133 #1: end-users have no business on the `/communities` collection
 * route — its resolvers fire org-scoped calls their JWT 403s, leaving endless
 * skeletons and a misleading "unexpected error" toast.
 *
 * They cannot simply be dropped from the module's `roleGuard`: `/communities`
 * is the app's first page (VITE_APP_FIRST_PAGE) and the satellite post-login
 * flow returns end-users to it, so a RoleGuard bounce to /access-denied would
 * dead-end a successful production login. Instead, redirect them to their own
 * home: the site dashboard when they own exactly one site, their member
 * aggregate otherwise. Staff (admin/supervisor) are unaffected.
 *
 * The guard owns the entities preflight: do not render the guarded module
 * until the destination can be decided, otherwise its resolvers fire the
 * doomed org-scoped calls before the redirect happens.
 */
export function EndUserCommunitiesRedirect({
  children,
}: EndUserCommunitiesRedirectProps) {
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user);
  const metadata = useSelector((state: any) => state.metadata);

  const role = getOrgRole(user);
  const entities = (metadata?.entities as any) || null;
  const sites: any[] = entities?.site || entities?.sites || [];
  const entitiesStatus = metadata?.EntitiesAsyncStatus;
  const ownMemberId =
    user?.metadata?.flexo_user_id || user?.tokenContent?.flexo_user_id;
  const entitiesReady = entitiesStatus === 'SUCCESS' || sites.length > 0;

  useEffect(() => {
    if (
      isEndUser(role) &&
      !entitiesReady &&
      entitiesStatus !== 'PENDING' &&
      entitiesStatus !== 'ERROR'
    ) {
      dispatch(METADATA.SetEntitiesAsync({ dispatch }) as any);
    }
  }, [dispatch, entitiesReady, entitiesStatus, role]);

  if (!isEndUser(role)) {
    return <>{children}</>;
  }

  if (!entitiesReady) {
    if (entitiesStatus === 'ERROR') {
      return (
        <Navigate
          to={ownMemberId ? `/member/${ownMemberId}` : '/access-denied'}
          replace
        />
      );
    }
    return null;
  }

  const singleSiteId = sites.length === 1 ? sites[0]?.siteId : null;
  if (singleSiteId) {
    return <Navigate to={`/site/${singleSiteId}`} replace />;
  }

  return (
    <Navigate
      to={ownMemberId ? `/member/${ownMemberId}` : '/access-denied'}
      replace
    />
  );
}
